import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import { movies } from "@/mocks/movies";
import FilterPanel from "./components/FilterPanel";

const PER_PAGE = 8;

const sortOptions = [
  { value: "latest", label: "最新上映" },
  { value: "rating", label: "评分最高" },
  { value: "popular", label: "热门程度" },
];

export default function Category() {
  const [searchParams] = useSearchParams();
  const genreParam = searchParams.get("genre");

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (genreParam) {
      setSelectedGenres([genreParam]);
    } else {
      setSelectedGenres([]);
    }
    setPage(1);
  }, [genreParam]);

  const toggle = (group: "genre" | "region" | "year", value: string) => {
    const setter =
      group === "genre" ? setSelectedGenres : group === "region" ? setSelectedRegions : setSelectedYears;
    setter((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
    );
    setPage(1);
  };

  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setPage(1);
  };

  const filtered = useMemo(() => {
    const result = movies.filter((movie) => {
      const genreMatch =
        selectedGenres.length === 0 || movie.genres.some((genre) => selectedGenres.includes(genre));
      const regionMatch = selectedRegions.length === 0 || selectedRegions.includes(movie.region);
      const yearMatch =
        selectedYears.length === 0 || selectedYears.includes(String(movie.year));
      return genreMatch && regionMatch && yearMatch;
    });

    const sorted = [...result];
    if (sort === "rating") {
      sorted.sort((a, b) => b.rating - a.rating);
    } else if (sort === "popular") {
      sorted.sort((a, b) => {
        if (!!a.featured !== !!b.featured) return a.featured ? -1 : 1;
        return b.rating - a.rating;
      });
    } else {
      sorted.sort((a, b) => b.year - a.year || b.rating - a.rating);
    }
    return sorted;
  }, [selectedGenres, selectedRegions, selectedYears, sort]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const currentPage = Math.min(page, Math.max(totalPages, 1));
  const pageMovies = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const activeGenre = selectedGenres.length === 1 ? selectedGenres[0] : null;

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 lg:px-10">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-xs text-foreground-600">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span>电影分类</span>
        {activeGenre && (
          <>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-300">{activeGenre}</span>
          </>
        )}
      </nav>

      <header className="mb-8">
        <h1 className="mb-3 font-heading text-3xl font-extrabold tracking-tight text-foreground-50 md:text-4xl">
          {activeGenre ? `${activeGenre}类午夜电影` : "电影分类"}
        </h1>
        <p className="max-w-3xl text-sm leading-relaxed text-foreground-500">
          {activeGenre
            ? `精选适合午夜观看的${activeGenre}类型影片，结合评分与观影氛围排列，点击封面查看完整资料与观看建议。`
            : "按类型、地区、年份多维筛选午夜影片，支持多种排序方式，帮你更快找到今夜想看的那一部。"}
        </p>
        <p className="mt-2 text-xs text-foreground-600">
          当前共匹配 <span className="font-semibold text-primary-400">{filtered.length}</span> 部影片（本站内容为演示数据）
        </p>
      </header>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200/80 bg-background-100 p-5">
            <FilterPanel
              selectedGenres={selectedGenres}
              selectedRegions={selectedRegions}
              selectedYears={selectedYears}
              onToggle={toggle}
              onReset={resetFilters}
            />
          </div>
        </aside>

        <div>
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-2 text-sm font-medium text-foreground-200 lg:hidden"
            >
              <i className="ri-filter-3-line text-primary-400"></i>
              筛选
              {(selectedGenres.length + selectedRegions.length + selectedYears.length) > 0 && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-[11px] font-bold text-background-50">
                  {selectedGenres.length + selectedRegions.length + selectedYears.length}
                </span>
              )}
            </button>

            <div className="ml-auto flex items-center gap-2">
              <span className="hidden text-xs text-foreground-600 sm:inline">排序：</span>
              <div className="flex items-center gap-1 rounded-md bg-background-100 p-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setSort(option.value);
                      setPage(1);
                    }}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      sort === option.value
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-400 hover:text-foreground-100"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {pageMovies.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {pageMovies.map((movie, index) => (
                  <MovieCard key={movie.id} movie={movie} index={index} />
                ))}
              </div>
              <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
              <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-200/70 text-3xl text-foreground-500">
                <i className="ri-moon-line"></i>
              </span>
              <h3 className="mb-2 font-heading text-lg font-bold text-foreground-100">
                没有找到符合条件的影片
              </h3>
              <p className="mb-6 max-w-sm text-sm text-foreground-600">
                试试放宽筛选条件，或者直接重置所有筛选，重新浏览全部午夜电影。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-background-50/80 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl border-t border-background-200 bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-foreground-50">筛选影片</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭筛选"
                className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-200"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <FilterPanel
              selectedGenres={selectedGenres}
              selectedRegions={selectedRegions}
              selectedYears={selectedYears}
              onToggle={toggle}
              onReset={resetFilters}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}